<template>
  <div>
    <div class="legend-container">
      <div class="legend-item">
        <span class="legend-color" style="background-color: #2B9DFD;"></span>
        <span class="legend-text">{{ $t('OperationPlanning.event.global') }}</span>
      </div>
      <div class="legend-item">
        <span class="legend-color" style="background-color: #FB9D00;"></span>
        <span class="legend-text">{{ $t('OperationHonoraire') }}</span>
      </div>
      <div class="legend-item">
        <span class="legende-mail"></span>
        <span class="legend-text">{{ $t('Alertes.mail.annonce.step') }}</span>
        <div class="explication">
          <b-icon id="info-circle-explanation" class="ml-2" icon="info-circle"
          ></b-icon>
          <b-tooltip aria-rowcount="3" html
                     target="info-circle-explanation" variant="dark">
            {{ this.$t("Alertes.mail.explication") }}
          </b-tooltip>
        </div>
      </div>
    </div>


    <div class="scale-selector">
      <b-button-group size="xs">
        <b-button :variant="scale === 'month' ? 'primary' : 'outline-primary'" class="small-button-scale" size="xs"
                  @click="setScale('month')">
          Mois
        </b-button>
        <b-button :variant="scale === 'week' ? 'primary' : 'outline-primary'" class="small-button-scale" size="xs"
                  @click="setScale('week')">
          Semaine
        </b-button>
        <b-button :variant="scale === 'day' ? 'primary' : 'outline-primary'" class="small-button-scale" size="xs"
                  @click="setScale('day')">
          Jour
        </b-button>
      </b-button-group>
    </div>

    <div
      ref="scrollContainer"
      class="timeline-wrapper mt-3"
      @mousedown="onMouseDown"
      @mouseleave="onMouseLeave"
      @mousemove="onMouseMove"
      @mouseup="onMouseUp"
    >
      <table v-show="!isLoading && showPlanning" class="timeline backwhite">
        <thead>
        <tr ref="yearsRow">
          <th class="resource-name column-left action-button">
            <div class="row justify-content-center">
              <b-button class="small-button" size="xs" variant="outline-primary" @click="previousYear">
                <b-icon-arrow-left/>
              </b-button>
              <b-button class="small-button" size="xs" variant="outline-primary" @click="today('smooth')">
                Aujourd'hui
              </b-button>
              <b-button class="small-button" size="xs" variant="outline-primary" @click="nextYear">
                <b-icon-arrow-right/>
              </b-button>
            </div>
          </th>
          <th
            v-for="(period, index) in periods"
            :key="getPeriodKey(period)"
            :class="getPeriodClass(period, index)"
          >
            <template v-if="shouldDisplayYear(period, index)">
              {{ formatPeriodHeader(period) }}
            </template>
          </th>
        </tr>
        <tr>
          <th class="resource-name resource-empty column-left"></th>
          <th
            v-for="period in periods"
            :key="getPeriodKey(period)"
            :class="{ 'current-month': isCurrentPeriod(period) }"
            class="month"
          >
            {{ formatPeriod(period) }}
          </th>
        </tr>
        </thead>
        <template v-if="operations && operations.length">
          <template v-for="operation in operations">

            <event-row
              :is-visible="isGeneralVisible(operation.id)"
              :title="'Général'"
              color="#6D84C4"
              @toggle="toggleGeneralVisibility(operation.id)"
            />

            <event-items
              :events="operation.general"
              :is-visible="isGeneralVisible(operation.id)"
              :scale="scale"
              @scroll-to-event="scrollToEvent"
              @update-color="updateColor"
            />

            <event-row
              :is-visible="isGeneralLotVisible(operation.id)"
              :title="'Général pour tous les lots'"
              color="#9E0430"
              @toggle="toggleGeneralLotVisibility(operation.id)"
            />

            <event-items
              :events="operation.generalLots"
              :has-sub-events="true"
              :is-visible="isGeneralLotVisible(operation.id)"
              :scale="scale"
              @add-sub-event="addSubPlageForEvent"
              @edit-sub-event="editSubPlage"
              @delete-sub-event="deleteSubPlage"
              @scroll-to-event="scrollToEvent"
              @update-color="updateColor"
            />

            <template v-for="lot in operation.operationLots">

              <event-row
                :is-visible="isLotVisible(lot.id)"
                :title="lot.tostring"
                color="#07153C"
                @toggle="toggleLotVisibility(lot.id)"
              />

              <event-items
                :events="lot.evenements"
                :is-visible="isLotVisible(lot.id)"
                :scale="scale"
                @scroll-to-event="scrollToEvent"
                @update-color="updateColor"
              />

              <event-items
                :events="lot.constructions"
                :has-sub-events="true"
                :is-visible="isLotVisible(lot.id)"
                :scale="scale"
                @add-sub-event="addSubPlageForEvent"
                @edit-sub-event="editSubPlage"
                @delete-sub-event="deleteSubPlage"
                @scroll-to-event="scrollToEvent"
                @update-color="updateColor"
              />

              <event-items
                :events="lot.honoraires"
                :is-visible="isLotVisible(lot.id)"
                :scale="scale"
                @scroll-to-event="scrollToEvent"
                @update-color="updateColor"
              />

            </template>
          </template>
        </template>
        <template v-else>
          <tr>
            <td class="resource-name resource-empty column-left"></td>
            <td v-for="period in periods" :key="getPeriodKey(period)" class="month"></td>
          </tr>
        </template>
      </table>
    </div>

    <div v-show="isLoading && !showPlanning" class="spinner-planning">
      <b-spinner label="Loading..." large></b-spinner>
    </div>

    <add-event-modal :custom-key="customKey" :evenement="this.selected.evenement"
                     :operation="this.selected.operation" :sub-plage="this.selected.subPlage"
                     @didAddEvenement="didAddEvenement" @didCloseModal="didCloseModal"
                     @didUpdateEvenement="didUpdateEvenement"/>

  </div>

</template>

<script>
import {
  addDays,
  addWeeks,
  differenceInDays,
  eachDayOfInterval,
  eachMonthOfInterval,
  eachWeekOfInterval,
  endOfWeek,
  endOfYear,
  format,
  getISOWeek,
  getMonth,
  getYear,
  isSameDay,
  isSameMonth,
  isSameWeek,
  isToday,
  startOfWeek,
  startOfYear
} from 'date-fns'
import {fr} from 'date-fns/locale'
import api from '../../services/api'
import Evenement from './Evenement'
import EventRowComponent from './EventRowComponent'
import AddEventModal from './AddEventModal'
import EventRow from './EventRow'
import EventItems from './EventItems.vue'

export default {

  name: 'OperationShowPlanningOperation',
  components: {
    Evenement,
    EventRowComponent,
    AddEventModal,
    EventRow,
    EventItems
  },
  props: {
    operations: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      refreshKey: 0,
      currentMonthAndYear: new Date(),
      isLoading: true,
      currentDate: new Date(),
      years: [],
      months: [],
      weeks: [],
      days: [],
      dragging: false,
      startX: 0,
      scrollLeft: 0,
      targetDate: new Date(),
      hoveredEventId: null,
      evenementType: [],
      showPlanning: false,
      resetMonth: false,
      visibleOperations: [],
      generalVisibility: {},
      generalLotsVisibility: {},
      lotVisibility: {},
      visibleSubEvents: {},
      customKey: 0,
      selected: {
        operationLot: null,
        operation: null,
        evenement: null,
        subPlage: null
      },
      scale: 'month'
    }
  },

  computed: {
    periods() {
      return this.scale === 'month' ? this.months : this.scale === "week" ? this.weeks : this.days
    },
    baseWidth() {
      if (this.scale === 'month') {
        return 60;
      } else if (this.scale === 'week') {
        return 100;
      } else if (this.scale === 'day') {
        return 40
      }
      return 60;
    },
  },
  watch: {
    operations: {
      immediate: true,
      async handler(newValue) {
        await this.getTypesEvenements()
        this.initializeStartYear()
        this.today('auto')
        if (newValue && newValue.length > 0) {
          newValue.forEach(operation => {
            this.$set(this.generalVisibility, operation.id, true)
          })
          if (newValue.length === 1) {
            this.visibleOperations.push(newValue[0].id)
          }
        }
      }
    }
  },


  async mounted() {
    if (this.operations.length > 0) {
      await this.getTypesEvenements()
      this.today('auto')
    }

  },

  methods: {

    scrollToEvent(event) {
      const eventDate = event.startEvent

      let periodIndex;
      if (this.scale === 'day') {
        periodIndex = this.periods.findIndex(day =>
          isSameDay(day, eventDate)
        );
      } else if (this.scale === 'week') {
        periodIndex = this.periods.findIndex(week =>
          isSameWeek(week, eventDate)
        );
      } else if (this.scale === 'month') {
        periodIndex = this.periods.findIndex(month =>
          isSameMonth(month, eventDate)
        );
      }

      if (periodIndex !== -1) {
        const cellWidth = this.baseWidth
        const scrollPosition = periodIndex * cellWidth;

        this.$refs.scrollContainer.scrollTo({
          left: scrollPosition - 100,
          behavior: 'smooth',
        });
      }
    },

    updateColor(event) {

      if (!event.hasOwnProperty("objectLinked")) {
        return
      }

      let data = new FormData()
      data.append('event_type', event.objectLinked.type)
      data.append('color', event.color)

      let url = '/operationConstruction/updateColor/' + event.objectLinked.id

      api({
        method: 'POST',
        url: url,
        data: data
      })
        .then(() => {
          if (event && event.hasOwnProperty('subPlages') && event.subPlages.length > 0) {
            event.subPlages.forEach(subPlage => {
              subPlage.color = event.color
            })
          }
        })
    },

    formatDate(date, formatStr = 'yyyy-MM-dd') {
      return format(date, formatStr);
    },

    getPeriodKey(period) {
      return period.getTime();
    },
    getPeriodClass(period, index) {
      let classToReturn = '';
      if (this.scale === 'month') {
        classToReturn = "year";
      } else if (this.scale === 'week') {
        classToReturn = `monthth`;
      } else if (this.scale === 'day') {
        classToReturn = 'dayth';
      }
      if (this.shouldDisplayYear(period, index) && (this.scale === "day" || this.scale === "month")) {
        classToReturn += ' custom-border-left-header';
      }
      return classToReturn;
    },

    isCurrentPeriod(period) {
      if (this.scale === 'month') {
        return this.isCurrentMonth(period);
      } else if (this.scale === 'week') {
        return this.isCurrentWeek(period);
      } else if (this.scale === 'day') {
        return isToday(period);
      }
      return false;
    },

    formatPeriod(period) {
      if (this.scale === 'month') {
        return this.format(period, 'MMM');
      } else if (this.scale === 'week') {
        return 'S.' + this.getISOWeek(period);
      } else if (this.scale === 'day') {
        return this.format(period, 'dd');
      }
      return '';
    },

    formatPeriodHeader(period) {
      if (this.scale === 'month') {
        return this.format(period, 'yyyy');
      } else if (this.scale === 'week') {
        return this.format(this.getEndOfWeek(period), 'MMM yyyy');
      } else if (this.scale === 'day') {
        return this.format(period, 'MMMM yyyy');
      }
      return '';
    },

    shouldDisplayYear(period, index) {
      if (this.scale === 'month') {
        return this.isNewYear(period, index);
      } else if (this.scale === 'week') {
        return this.isNewMonthOrYear(this.getEndOfWeek(period), index);
      } else if (this.scale === 'day') {
        return this.isNewMonthOrYear(period, index);
      }
      return false;
    },


    getISOWeek(week) {
      return getISOWeek(week);
    },
    isNewMonthOrYear(week, index) {
      if (index === 0) {
        return true
      }

      const previousWeek = this.scale === "week" ? this.getEndOfWeek(this.periods[index - 1]) : this.periods[index - 1];
      const currentMonth = week.getMonth();
      const previousMonth = previousWeek.getMonth();
      const currentYear = week.getFullYear();
      const previousYear = previousWeek.getFullYear();

      return currentMonth !== previousMonth || currentYear !== previousYear;
    },


    isNewYear(month, index) {
      if (index === 0) {
        return true
      }
      const previousMonths = this.months[index - 1];
      const currentYear = month.getFullYear();
      const previousYear = previousMonths.getFullYear();
      return currentYear !== previousYear
    },

    isCurrentWeek(week) {
      const currentDate = new Date();
      return isSameWeek(week, currentDate, {locale: fr, weekStartsOn: 1});
    },


    setScale(newScale) {
      this.scale = newScale;
      this.initializeStartYear()
      this.today('auto')
    },

    didAddEvenement(evenement) {
      this.selected.evenement.subPlages.push(evenement)
      this.selected.evenement = null
      this.selected.subPlage = null
    },

    didUpdateEvenement(evenement) {
      this.selected.evenement.subPlages = this.selected.evenement.subPlages.map(subPlage => {
        if (subPlage.id === evenement.id) {
          return evenement
        }
        return subPlage
      })
      this.selected.evenement = null
      this.selected.subPlage = null
    },

    didCloseModal() {
      this.selected.evenement = null
      this.selected.subPlage = null
    },

    async deleteSubPlage(event, subPlageId) {
      try {
        await api.delete(`/sousPlageEvenement/` + subPlageId);
        this.successToast(event.subPlages.find(subPlage => subPlage.id === subPlageId).name);
        event.subPlages = event.subPlages.filter(subPlage => subPlage.id !== subPlageId);

      } catch (error) {
      }
    },

    successToast(name) {
      this.$bvToast.toast(this.$i18n.t('Event') + ' "' + name + '" ' + this.$i18n.t('deleted'), {
        autoHideDelay: 4000,
        toaster: 'b-toaster-bottom-right',
        noCloseButton: true,
        variant: "success",
        solid: true,
      })
    },


    toggleSubEvents(eventId) {
      this.$set(this.visibleSubEvents, eventId, !this.visibleSubEvents[eventId]);
    },
    areSubEventsVisible(eventId) {
      return !!this.visibleSubEvents[eventId];
    },

    async addSubPlageForEvent(event, operation) {
      this.customKey++
      this.selected.evenement = event
      this.selected.operation = operation
      this.$bvModal.show('add-event-modal')
    },

    async editSubPlage(subPlage, event, operation) {
      this.customKey++
      this.selected.evenement = event
      this.selected.operation = operation
      this.selected.subPlage = subPlage
      this.$bvModal.show('add-event-modal')
    },

    toggleGeneralVisibility(operationId) {
      if (this.generalVisibility[operationId]) {
        this.$set(this.generalVisibility, operationId, false)
      } else {
        this.$set(this.generalVisibility, operationId, true)
      }
    },
    isGeneralVisible(operationId) {
      return !!this.generalVisibility[operationId]
    },


    toggleGeneralLotVisibility(operationId) {
      if (this.generalLotsVisibility[operationId]) {
        this.$set(this.generalLotsVisibility, operationId, false)
      } else {
        this.$set(this.generalLotsVisibility, operationId, true)
      }
    },
    isGeneralLotVisible(operationId) {
      return !!this.generalLotsVisibility[operationId]
    },

    toggleLotVisibility(lotId) {
      if (this.lotVisibility[lotId]) {
        this.$set(this.lotVisibility, lotId, false)
      } else {
        this.$set(this.lotVisibility, lotId, true)
      }
    },
    isLotVisible(lotId) {
      return !!this.lotVisibility[lotId]
    },

    toggleOperation(operationId) {
      const index = this.visibleOperations.indexOf(operationId)
      if (index === -1) {
        this.visibleOperations.push(operationId)
      } else {
        this.visibleOperations.splice(index, 1)
      }
    },
    addYears(yearCount) {
      const lastYear = this.years[this.years.length - 1];
      const newStartYear = lastYear + 1;
      const newEndYear = lastYear + yearCount;

      const newMonths = eachMonthOfInterval({
        start: startOfYear(new Date(newStartYear, 0, 1)),
        end: endOfYear(new Date(newEndYear, 11, 31)),
      });

      const newWeeks = eachWeekOfInterval({
        start: startOfYear(new Date(newStartYear, 0, 1)),
        end: endOfYear(new Date(newEndYear, 11, 31)),
      }, {weekStartsOn: 1});

      const newDays = eachDayOfInterval({
        start: startOfYear(new Date(newStartYear, 0, 1)),
        end: endOfYear(new Date(newEndYear, 11, 31)),
      });

      this.months = [...this.months, ...newMonths];
      this.weeks = [...this.weeks, ...newWeeks];
      this.years = [...this.years, ...Array.from({length: yearCount}, (v, k) => newStartYear + k)];
      this.days = [...this.days, ...newDays];

    },


    initializeStartYear() {
      const allEvents = this.getAllEvents()

      let startYear, endYear

      if (allEvents.length > 0) {
        const validEvents = allEvents.filter(event => event.startEvent !== null)
        if (validEvents.length > 0) {
          const earliestEvent = validEvents.reduce((prev, current) => new Date(prev.startEvent) < new Date(current.startEvent) ? prev : current)
          const latestEvent = validEvents.reduce((prev, current) => {
            const prevDate = prev.endEvent ? new Date(prev.endEvent) : new Date(prev.startEvent)
            const currentDate = current.endEvent ? new Date(current.endEvent) : new Date(current.startEvent)
            return prevDate > currentDate ? prev : current
          })

          startYear = new Date(earliestEvent.startEvent).getFullYear()
          endYear = (latestEvent.endEvent ? new Date(latestEvent.endEvent) : new Date(latestEvent.startEvent)).getFullYear()
        } else {
          const currentYear = new Date().getFullYear()
          startYear = currentYear - 1
          endYear = currentYear + 1
        }
      } else {
        const currentYear = new Date().getFullYear()
        startYear = currentYear - 1
        endYear = currentYear + 5
      }
      endYear += 1


      this.months = eachMonthOfInterval({
        start: startOfYear(new Date(startYear, 0, 1)),
        end: endOfYear(new Date(endYear, 11, 31)),
      });

      this.years = Array.from(new Set(this.months.map(date => date.getFullYear())));
      this.weeks = eachWeekOfInterval({
        start: startOfYear(new Date(startYear, 0, 1)),
        end: endOfYear(new Date(endYear, 11, 31)),
      }, {weekStartsOn: 1});


      this.days = eachDayOfInterval({
        start: startOfYear(new Date(startYear, 0, 1)),
        end: endOfYear(new Date(endYear, 11, 31)),
      });
    },

    getStartOfWeek(date) {
      return startOfWeek(date, {weekStartsOn: 1})
    },

    getEndOfWeek(date) {
      return endOfWeek(date, {weekStartsOn: 1});
    },

    addDays(date, days) {
      const result = new Date(date);
      result.setDate(result.getDate() + days);
      return result;
    },


    getAllEvents() {
      let events = []

      this.operations.forEach(operation => {
        events.push(...operation.general.filter(event => event.startEvent !== null))
        events.push(...operation.generalLots.filter(event => event.startEvent !== null))

        operation.operationLots.forEach(operationLot => {
          events.push(...operationLot.evenements.filter(event => event.startEvent !== null))
        })
      })

      return events
    },

    async getTypesEvenements() {
      await api
        .get(`/evenementPlanning/getEvenementTypes`)
        .then(response => {
          this.evenementType = response.data
        })
    },

    currentProgressPercentage(period) {
      let now = new Date();
      if (this.scale === 'month') {
        const dayOfMonth = now.getDate();
        const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
        return (dayOfMonth / daysInMonth) * 100;
      } else if (this.scale === 'week') {
        let nowInstant = now;
        return (differenceInDays(nowInstant, startOfWeek(now, {weekStartsOn: 1})) / 7) * 100;
      } else if (this.scale === 'day') {
        const hoursInDay = 24;
        const currentHour = now.getHours() + now.getMinutes() / 60;
        return (currentHour / hoursInDay) * 100;
      }
      return 0;
    },

    isCurrentMonth(month) {
      const paramsMonth = new Date(month)
      const currentMonth = new Date(this.currentMonthAndYear)
      return paramsMonth.getMonth() === currentMonth.getMonth() && paramsMonth.getFullYear() === currentMonth.getFullYear()
    },

    isEventThisMonth(month, event) { //ne pas enlever
      const paramsMonth = new Date(month)
      const eventMonth = new Date(event.startEvent)
      return paramsMonth.getMonth() === eventMonth.getMonth() && paramsMonth.getFullYear() === eventMonth.getFullYear()
    },

    isEventThisWeek(week, event) { //ne pas enlever
      const eventDate = new Date(event.startEvent);
      return isSameWeek(week, eventDate, {locale: fr, weekStartsOn: 1});
    },

    isEventThisDay(day, event) {
      const eventDate = new Date(event.startEvent);
      return isSameDay(day, eventDate)
    },

    format(date, formatStr) {
      return format(date, formatStr, {locale: fr})
    },

    filteredEvents(id, month, isOperation = true, arrayKey) {
      let events = []

      if (isOperation) {
        const operation = this.operations.find(operation => operation.id === id)
        if (operation) {
          events = operation[arrayKey]
        }
      } else {
        const operationWithLot = this.operations.find(operation =>
          operation.operationLots.some(lot => lot.id === id)
        )

        if (operationWithLot) {
          const lot = operationWithLot.operationLots.find(lot => lot.id === id)
          events = lot.constructions
        }
      }
      if (events === undefined) {
        events = []
      }

      return events.filter(event => {
        if (event.startEvent == null) {
          return false
        }
        const eventStartDate = new Date(event.startEvent)
        const eventStartMonth = eventStartDate.getMonth()
        const eventStartYear = eventStartDate.getFullYear()

        return eventStartMonth === month.getMonth() && eventStartYear === month.getFullYear()
      })
    },

    onMouseDown(e) {
      this.dragging = true
      this.startX = e.pageX - this.$refs.scrollContainer.offsetLeft
      this.scrollLeft = this.$refs.scrollContainer.scrollLeft
    },
    onMouseLeave() {
      this.dragging = false
    },
    onMouseUp() {
      this.dragging = false
      this.checkScrollPosition()
    },
    onMouseMove: function (e) {
      if (!this.dragging) return;
      e.preventDefault();

      const x = e.pageX - this.$refs.scrollContainer.offsetLeft;
      const walkX = x - this.startX;
      this.$refs.scrollContainer.scrollLeft = this.scrollLeft - walkX;

      this.checkScrollPosition();

    },


    checkScrollPosition() {
      const scrollContainer = this.$refs.scrollContainer;
      const nearEnd = scrollContainer.scrollLeft >= scrollContainer.scrollWidth - scrollContainer.offsetWidth - 2000;
      if (nearEnd) {
        this.addYears(1);
      }
    },


    today(behavior) {
      this.targetDate = new Date();
      this.$nextTick(() => {
        if (this.scale === 'week') {
          const currentWeekIndex = this.weeks.findIndex(week => this.isCurrentWeek(week));
          if (currentWeekIndex >= 0) {
            if (this.$refs.scrollContainer) {
              this.$refs.scrollContainer.scrollTo({
                left: currentWeekIndex === 0 ? currentWeekIndex * 100 : ((currentWeekIndex - 1) * 100),
                behavior: behavior
              });
            }
          }
        } else if (this.scale === 'month') {
          this.updateScroll(behavior);
        } else if (this.scale === 'day') {
          this.updateScroll(behavior);
        }
        this.showPlanning = true;
        this.isLoading = false;
      });
    },


    nextYear() {
      if (this.scale === 'month') {
        this.targetDate.setFullYear(this.targetDate.getFullYear() + 1);
        const nextYear = this.targetDate.getFullYear();
        const alreadyExists = this.years.includes(nextYear);
        if (!alreadyExists) {
          this.addYears(1);
        }
        this.targetDate.setMonth(0);
        this.resetMonth = true;
      } else if (this.scale === 'week') {
        this.targetDate = addWeeks(this.targetDate, 8);
      } else if (this.scale === 'day') {
        this.targetDate = addDays(this.targetDate, 30);
      }
      this.updateScroll('smooth');
    },

    previousYear() {
      if (this.scale === 'month') {
        this.targetDate.setFullYear(this.targetDate.getFullYear() - 1);
        this.targetDate.setMonth(0);
        this.resetMonth = true;
      } else if (this.scale === 'week') {
        this.targetDate = addWeeks(this.targetDate, -8);
      } else if (this.scale === 'day') {
        this.targetDate = addDays(this.targetDate, -30);
      }
      this.updateScroll('smooth');
    },

    updateScroll(behavior) {
      if (this.scale === 'month') {
        if (this.resetMonth) {
          this.targetDate.setMonth(0);
        }
        const currentYear = this.targetDate.getFullYear();
        const currentMonth = this.targetDate.getMonth();
        const startMonthIndex = this.months.findIndex(
          (month) => getYear(month) === currentYear && getMonth(month) === currentMonth
        );
        let targetMonthIndex = !this.resetMonth ? startMonthIndex - 2 : startMonthIndex;
        if (targetMonthIndex >= 0) {
          if (this.$refs.scrollContainer) {
            this.$refs.scrollContainer.scrollTo({
              left: targetMonthIndex * 60,
              behavior: behavior
            });
          }
        }
      } else if (this.scale === 'week') {
        const currentWeek = startOfWeek(this.targetDate, {weekStartsOn: 1});
        let targetWeekIndex = this.weeks.findIndex(
          (week) => isSameWeek(week, currentWeek, {weekStartsOn: 1})
        );
        if (targetWeekIndex >= 0) {
          if (this.$refs.scrollContainer) {
            this.$refs.scrollContainer.scrollTo({
              left: targetWeekIndex * 100,
              behavior: behavior
            });
          }
        }
      } else if (this.scale === 'day') {
        const currentDayIndex = this.days.findIndex(
          (day) =>
            day.getDate() === this.targetDate.getDate() &&
            day.getMonth() === this.targetDate.getMonth() &&
            day.getFullYear() === this.targetDate.getFullYear()
        );
        if (currentDayIndex >= 0) {
          if (this.$refs.scrollContainer) {
            this.$refs.scrollContainer.scrollTo({
              left: (currentDayIndex * 40) - 90,
              behavior: behavior,
            });
          }
        }
      }
    }


  }
}
</script>

<style scoped>
@import '../../assets/css/timeline.css';

.backwhite {
  background-color: #FFFFFF;
}

.tr-title {
  text-align: left;
  font-weight: 500;
  letter-spacing: 0px;
  color: #FFFFFF;
  opacity: 1;
}

svg[aria-label="chevron right"],
svg[aria-label="chevron down"] {
  color: #FFFFFF;
}

.legend-container {
  display: flex;
  justify-content: start;
  gap: 4em;
  margin-bottom: 1rem;
}

.legend-item {
  display: flex;
  align-items: center;
}

.legend-color {
  width: 28px;
  height: 18px;
  border-radius: 2px;
  margin-right: 1rem;
}

.legende-mail {
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background-color: var(--main-red);
  border: 2px solid var(--main-color);
  margin-right: 1rem;
  z-index: 1;
}

.legend-text {
  font-size: 14px;
}

.explication {
  color: var(--tertiary-text-color);
  font-size: 14px;
  font-family: "Montserrat-Regular", "Arial", sans-serif;
  display: flex;
  justify-content: start;
  align-items: center;
}

svg[aria-label="info circle"] {
  width: 1.25em;
  height: 1.25em;
  margin-right: 0.5em;
}

.toggle-sub-events-button {
  cursor: pointer;
  margin-left: 5px;
  transition: transform 0.3s;
}

.toggle-sub-events-button.rotated {
  transform: rotate(90deg);
}

.scale-selector {
  margin-bottom: 20px;
  margin-top: 20px;
}

.custom-border-left-header {
  border-left: 1px solid var(--main-color);
  padding-left: 1em;
}


</style>



// WEBPACK FOOTER //
// src/views/operationPlanning/OperationShowPlanningOperation.vue